import { Api } from "services/api/api.service";
import type { StaffProfileData } from "services/api/company/staff.service";
import type { TranslateFn } from "./profilePanel.state.contract";
import {
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "web-apps/company/components/staff/components/shared/staff.paymentMethod.logic";
import type { PaymentMethodDetailsForm } from "../profilePanel.contract";

interface SavePaymentMethodParams {
  companyId: number | null;
  editedPaymentMethodDetails: PaymentMethodDetailsForm;
  executeSavingAction: (action: () => Promise<void>) => void;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setSaveError: (value: string) => void;
  closeModal: () => void;
  translate: TranslateFn;
  workerId: number | null;
}

interface DeletePaymentMethodParams {
  companyId: number | null;
  executeSavingAction: (action: () => Promise<void>) => void;
  isSaving: boolean;
  onWorkerUpdated?: () => void;
  profile: StaffProfileData | null;
  reloadProfile: () => void;
  setSaveError: (value: string) => void;
  closeModal: () => void;
  translate: TranslateFn;
  workerId: number | null;
}

export const hasPaymentMethodDetailsChanged = (
  editedPaymentMethodDetails: PaymentMethodDetailsForm,
  paymentMethodInitialDetails: PaymentMethodDetailsForm,
): boolean => {
  return (
    JSON.stringify(editedPaymentMethodDetails) !==
    JSON.stringify(paymentMethodInitialDetails)
  );
};

export const resolvePaymentMethodError = (
  editedPaymentMethodDetails: PaymentMethodDetailsForm,
  hasTouchedPaymentMethodValue: boolean,
  translate: TranslateFn,
): string => {
  const validationResult = validatePaymentMethodValue(
    editedPaymentMethodDetails.type,
    editedPaymentMethodDetails.value,
  );

  if (validationResult.type === "success") {
    return "";
  }

  if (
    editedPaymentMethodDetails.value.trim().length === 0 &&
    !hasTouchedPaymentMethodValue
  ) {
    return "";
  }

  return translate(
    toPaymentMethodValidationTranslationKey(validationResult.error),
  );
};

export const canSavePaymentMethod = (
  editedPaymentMethodDetails: PaymentMethodDetailsForm,
): boolean => {
  return (
    validatePaymentMethodValue(
      editedPaymentMethodDetails.type,
      editedPaymentMethodDetails.value,
    ).type === "success"
  );
};

export const savePaymentMethod = (params: SavePaymentMethodParams): void => {
  if (params.isSaving) return;
  if (params.companyId == null || params.workerId == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const validationResult = validatePaymentMethodValue(
    params.editedPaymentMethodDetails.type,
    params.editedPaymentMethodDetails.value,
  );

  if (validationResult.type === "failure") {
    params.setSaveError(
      params.translate(
        toPaymentMethodValidationTranslationKey(validationResult.error),
      ),
    );
    return;
  }

  const paymentMethod = params.profile?.paymentMethods[0] ?? null;
  const companyId = params.companyId;
  const workerId = params.workerId;

  params.executeSavingAction(async () => {
    if (paymentMethod == null) {
      await Api().company.staff.addWorkerPaymentMethod(companyId, workerId, {
        type: params.editedPaymentMethodDetails.type,
        value: validationResult.data,
      });
    } else {
      await Api().company.staff.updateWorkerPaymentMethod(
        companyId,
        workerId,
        paymentMethod.id,
        {
          type: params.editedPaymentMethodDetails.type,
          value: validationResult.data,
        },
      );
    }

    params.setSaveError("");
    params.closeModal();
    params.reloadProfile();
    params.onWorkerUpdated?.();
  });
};

export const deletePaymentMethod = (
  params: DeletePaymentMethodParams,
): void => {
  if (params.isSaving) return;

  const paymentMethod = params.profile?.paymentMethods[0] ?? null;
  if (
    params.companyId == null ||
    params.workerId == null ||
    paymentMethod == null
  ) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }

  const companyId = params.companyId;
  const workerId = params.workerId;

  params.executeSavingAction(async () => {
    await Api().company.staff.deleteWorkerPaymentMethod(
      companyId,
      workerId,
      paymentMethod.id,
    );

    params.setSaveError("");
    params.closeModal();
    params.reloadProfile();
    params.onWorkerUpdated?.();
  });
};
